import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Search, ShieldCheck, Sparkles, Users, FlaskConical, Microscope, Stethoscope, Pill, BarChart3, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import JobCard from "@/components/site/JobCard";
import StatCounter from "@/components/site/StatCounter";
import Reveal from "@/components/site/Reveal";
import MolecularBackground from "@/components/site/MolecularBackground";
import WaveDivider from "@/components/site/WaveDivider";
import TiltCard from "@/components/site/TiltCard";
import HexParticles from "@/components/site/HexParticles";
import TrustedByStrip from "@/components/site/TrustedByStrip";
import { useSEO } from "@/lib/seo";
import { useSiteSettings } from "@/lib/settings";
import { HOME_DEFAULTS, pick, renderInlineBold } from "@/lib/cms-defaults";
import { getIcon } from "@/lib/icon-map";

// H2 title size class — bumped by ~2px (was 32/38, now 34/40)
const H2_TITLE = "font-heading text-[34px] font-semibold text-slate-900 sm:text-[40px] leading-tight";

// Hero CTA button label size — bumped +2px vs default (text-sm → text-base)
const HERO_CTA_TXT = "text-base font-semibold";

const ECOSYSTEM_REMOVED = true; // Kept as flag: section removed per user request.

const SERVICES_FALLBACK = [
  { title: "Senior Management", description: "Vision, strategy & long-term growth", icon: "Crown" },
  { title: "Middle Management", description: "The bridge between strategy and execution", icon: "Users" },
  { title: "Junior & Entry-level Professionals", description: "The foundation of every pharma organisation", icon: "Sparkles" },
];

const ADVANTAGE_FALLBACK = [
  { k: "Network Access", g: "Broad, generic candidate pools.", p: "Deep, highly specialised niche Pharma / Biotech network across India and global markets." },
  { k: "Process Approach", g: "Volume-based resume forwarding.", p: "Customised, client-centric executive search and precision vetting." },
  { k: "Industry Knowledge", g: "Surface-level keyword matching.", p: "Inside-out understanding of technical, regulatory and cultural fit." },
  { k: "Outcome Focus", g: "Filling immediate vacancies.", p: "Forging long-term partnerships for sustainable organisational growth." },
];

export default function Home() {
  const { settings } = useSiteSettings();
  const c = settings?.home_content || {};
  const t = (k) => pick(c, HOME_DEFAULTS, k);
  const hireBtn = settings?.hire_talent_button_text || "Hire Talent";

  useSEO({
    title: "Specialised Pharma Recruitment",
    description: "PharmacoHires is a specialised pharma recruitment agency connecting scientific talent with leading pharma, biotech and CRO organisations.",
    image: t("hero_image"),
    pageKey: "home",
  });

  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [services, setServices] = useState(SERVICES_FALLBACK);

  useEffect(() => {
    if (!faqs.length) return;
    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
    };
    let el = document.getElementById("home-faq-jsonld");
    if (!el) { el = document.createElement("script"); el.id = "home-faq-jsonld"; el.type = "application/ld+json"; document.head.appendChild(el); }
    el.textContent = JSON.stringify(data);
    return () => { const e = document.getElementById("home-faq-jsonld"); if (e) e.remove(); };
  }, [faqs]);

  useEffect(() => {
    const asArray = (v) => (Array.isArray(v) ? v : []);
    api.get("/jobs", { params: { page: 1, page_size: 6 } })
      .then((r) => setJobs(asArray(r.data?.items))).catch(() => setJobs([]));
    api.get("/stats").then((r) => setStats(r.data && typeof r.data === "object" ? r.data : null)).catch(() => setStats(null));
    api.get("/testimonials", { params: { page: 1, page_size: 3 } }).then((r) => setTestimonials(asArray(r.data?.items))).catch(() => setTestimonials([]));
    api.get("/faqs").then((r) => setFaqs(asArray(r.data).slice(0, 5))).catch(() => setFaqs([]));
    api.get("/service-categories").then((r) => { const arr = asArray(r.data); if (arr.length) setServices(arr); }).catch(() => {});
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="hero-glow relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="absolute inset-0 bg-grid-light opacity-60" />
        <div className="container-page relative grid gap-12 py-16 md:grid-cols-12 md:py-24">
          <div className="md:col-span-7">
            <Reveal variant="up" className="overline mb-5">{t("hero_overline")}</Reveal>
            <Reveal as="h1" variant="up" delay={80} className="font-heading text-[38px] font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-[54px] lg:text-[62px]">
              <span className="text-shimmer">{t("hero_title_1")}</span>
              <span className="text-secondary"> {t("hero_title_2")}</span>
            </Reveal>
            <Reveal variant="up" delay={160} as="p" className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600 text-balance">
              {t("hero_subtitle")}
            </Reveal>
            <Reveal variant="up" delay={240} className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/jobs" data-testid="hero-cta-find-job">
                <Button size="lg" className={`h-12 bg-primary px-7 shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-primary/30 ${HERO_CTA_TXT}`}>
                  <Search className="mr-2 h-4 w-4" /> {t("hero_cta_find_job")}
                </Button>
              </Link>
              <Link to="/hire-talent" data-testid="hero-cta-hire-talent">
                <Button size="lg" variant="outline" className={`h-12 border-2 border-secondary px-7 text-secondary transition-all hover:-translate-y-0.5 hover:bg-secondary/5 ${HERO_CTA_TXT}`}>
                  {t("hero_cta_hire_talent")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </Reveal>

            {stats && (
              <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {[
                  { v: stats.placements, l: "Placements" },
                  { v: stats.clients, l: "Clients" },
                  { v: stats.industries, l: "Sub-sectors" },
                  { v: stats.years_experience, l: "Years" },
                ].map((s, i) => (
                  <Reveal key={s.l} variant="up" delay={320 + i * 80} className="rounded-lg border border-slate-200 bg-white p-4 hover-lift hover:border-primary/30">
                    <div className="font-heading text-3xl font-bold text-slate-900"><StatCounter value={s.v} /></div>
                    <div className="mt-1 text-xs uppercase tracking-wider text-slate-500">{s.l}</div>
                    <div className="mt-3 h-1 overflow-hidden rounded-full bg-slate-100">
                      <div className="stat-bar h-full rounded-full" style={{ background: `linear-gradient(90deg, hsl(var(--primary)), hsl(var(--secondary)))`, animationDelay: `${400 + i * 120}ms` }} />
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </div>

          <div className="md:col-span-5">
            <Reveal variant="right" delay={200} className="relative">
              <MolecularBackground className="opacity-70" />
              <div className="absolute -left-4 -top-4 hidden h-24 w-24 rounded-2xl bg-secondary/15 md:block animate-float-slow" />
              <div className="absolute -bottom-4 -right-4 hidden h-32 w-32 rounded-2xl bg-primary/10 md:block animate-float-slow" style={{ animationDelay: "800ms" }} />
              <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
                <img src={t("hero_image")} alt="Pharmaceutical scientist in a laboratory" loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] hover:scale-105" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <TrustedByStrip />

      {/* INDUSTRIES section — removed per user request */}

      {/* SERVICES */}
      <section className="section-pad bg-white">
        <div className="container-page">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div className="max-w-xl">
              <div className="overline mb-3">{t("services_overline")}</div>
              <h2 className={H2_TITLE}>{t("services_title")}</h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
              All services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {services.map((svc, i) => {
              const Icon = getIcon(svc.icon, "Users");
              const desc = svc.tagline || svc.description || svc.desc;
              return (
                <Reveal key={svc.id || svc.title} variant="up" delay={i * 90}>
                  <TiltCard testId={`service-${(svc.title || "").toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className="group h-full rounded-xl border border-slate-200 bg-white p-6 hover:border-primary/40">
                    <div className="mb-4 inline-grid h-12 w-12 place-items-center rounded-lg bg-secondary/15 text-secondary transition-transform group-hover:scale-110">
                      <Icon className="h-6 w-6 icon-wiggle" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900">{svc.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600 line-clamp-3">{desc}</p>
                  </TiltCard>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PHARMA TALENT ECOSYSTEM — removed per user request */}

      {/* PULL QUOTE */}
      <WaveDivider from="#ffffff" to="#0f172a" />
      <section className="relative bg-slate-900 py-20 text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-30" />
        <HexParticles />
        <div className="container-page relative">
          <Reveal className="mx-auto max-w-4xl text-center">
            <Quote className="mx-auto h-10 w-10 text-secondary" />
            <blockquote className="mt-6 font-heading text-[34px] font-semibold leading-tight sm:text-[42px] md:text-[54px]">
              {t("pull_quote")}
            </blockquote>
            <div className="mt-6 text-xs font-semibold uppercase tracking-widest text-white/70">— {t("pull_quote_author")}</div>
          </Reveal>
        </div>
      </section>
      <WaveDivider from="#0f172a" to="#f8fafc" flip />

      {/* FEATURED JOBS */}
      <section className="section-pad bg-slate-50">
        <div className="container-page">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div className="max-w-xl">
              <div className="overline mb-3">{t("featured_jobs_overline")}</div>
              <h2 className={H2_TITLE}>{t("featured_jobs_title")}</h2>
            </div>
            <Link to="/jobs" data-testid="home-view-all-jobs">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary/5">View all openings</Button>
            </Link>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {jobs.map((job, i) => (
              <Reveal key={job.id} variant="up" delay={i * 70}><JobCard job={job} testIdPrefix="featured-job" /></Reveal>
            ))}
            {jobs.length === 0 && (
              <div className="col-span-full rounded-xl border border-dashed border-slate-300 p-12 text-center text-slate-500">
                No active roles right now. Drop your resume and we will reach out.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS PREVIEW */}
      <section className="section-pad bg-white">
        <div className="container-page">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-5">
              <div className="overline mb-3">{t("testimonials_overline")}</div>
              <h2 className={H2_TITLE}>{t("testimonials_title")}</h2>
              <p className="mt-4 text-slate-600">{t("testimonials_body")}</p>
              <Link to="/testimonials" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                Read all stories <ArrowRight className="h-4 w-4" />
              </Link>
              <div className="mt-8 hidden overflow-hidden rounded-2xl border border-slate-200 md:block">
                <img src={t("testimonials_image")} alt="Business handshake" className="aspect-[4/3] w-full object-cover" />
              </div>
            </div>
            <div className="grid gap-5 md:col-span-7">
              {testimonials.map((tm, i) => (
                <Reveal key={tm.id} variant="right" delay={i * 90} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm hover-lift" data-testid={`home-testimonial-${tm.id}`}>
                  <div className="text-sm leading-relaxed text-slate-700">“{tm.content}”</div>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 font-semibold text-primary">{tm.name.charAt(0)}</div>
                    <div>
                      <div className="font-semibold text-slate-900">{tm.name}</div>
                      <div className="text-xs text-slate-500">{tm.role}{tm.role && tm.company ? " · " : ""}{tm.company}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STRATEGIC ADVANTAGE */}
      <section className="section-pad bg-white">
        <div className="container-page">
          <Reveal className="mx-auto max-w-2xl text-center">
            <div className="overline mb-3">{t("strategic_overline")}</div>
            <h2 className={H2_TITLE}>{t("strategic_title")}</h2>
            <p className="mt-4 text-slate-600">{t("strategic_body")}</p>
          </Reveal>
          {(() => {
            const sa = settings?.strategic_advantage || {};
            const rows = Array.isArray(sa.rows) && sa.rows.length ? sa.rows : ADVANTAGE_FALLBACK;
            const brandName = `${settings?.brand_left || "Pharmaco"} ${settings?.brand_right || "Hires"}`;
            const colFeatureHeader = sa.col_feature_header || "";
            const colGeneralistHeader = sa.col_generalist_header || "Generalist Agencies";
            const colUsHeader = sa.col_us_header || brandName;
            return (
              <div className="mt-10 overflow-hidden rounded-2xl border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="border-b bg-slate-50 text-left">
                    <tr>
                      <th className="w-1/4 px-5 py-4 text-xs font-semibold uppercase tracking-widest text-slate-500">{colFeatureHeader}</th>
                      <th className="px-5 py-4 text-xs font-semibold uppercase tracking-widest text-slate-500">{colGeneralistHeader}</th>
                      <th className="px-5 py-4 text-xs font-semibold uppercase tracking-widest text-primary">{colUsHeader}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <Reveal key={`${row.k}-${i}`} as="tr" variant="up" delay={i * 90} className="advantage-row border-b last:border-0 align-top" data-testid={`advantage-row-${i + 1}`}>
                        <td className="bg-slate-50/50 px-5 py-5 font-heading font-semibold text-slate-900">{row.k}</td>
                        <td className="px-5 py-5 text-slate-600">{row.g}</td>
                        <td className="px-5 py-5 font-medium text-slate-900">
                          <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-secondary align-middle" />{row.p}
                        </td>
                      </Reveal>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad bg-slate-50">
        <div className="container-page grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="overline mb-3">{t("faq_overline")}</div>
            <h2 className={H2_TITLE}>{t("faq_title")}</h2>
            <p className="mt-4 text-slate-600">
              {t("faq_body")} <Link to="/contact" className="font-semibold text-primary hover:underline">Contact us</Link>
            </p>
          </div>
          <div className="md:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="home-faq">
              {faqs.map((f) => (
                <AccordionItem key={f.id} value={f.id}>
                  <AccordionTrigger className="text-left text-base font-semibold text-slate-900">{f.question}</AccordionTrigger>
                  <AccordionContent className="text-slate-600">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-primary-foreground">
        <div className="container-page grid gap-8 py-16 md:grid-cols-12 md:items-center">
          <div className="md:col-span-8">
            <div className="text-xs font-semibold uppercase tracking-widest text-white/70">{t("final_cta_overline")}</div>
            <h2 className="mt-2 font-heading text-[34px] font-semibold sm:text-[40px] leading-tight">{t("final_cta_title")}</h2>
            <p className="mt-3 max-w-2xl text-white/85">{t("final_cta_body")}</p>
          </div>
          <div className="flex flex-wrap gap-3 md:col-span-4 md:justify-end">
            <Link to="/hire-talent" data-testid="bottom-cta-hire">
              <Button size="lg" className="h-12 bg-secondary text-secondary-foreground hover:bg-secondary/90">{hireBtn}</Button>
            </Link>
            <Link to="/upload-resume" data-testid="bottom-cta-upload">
              <Button size="lg" variant="outline" className="h-12 border-white text-white hover:bg-white/10">Upload Resume</Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
